import React from "react";
import ServicePlanPriceIntervals from "./ServicePlanPriceIntervals";
import { VehicleModelData, VehicleModel, CmsTab } from "../models/ServicePlanModels";
import { observer } from "mobx-react";
import ServicePlanReactSelectWrap from "./ServicePlanReactSelectWrap";

export interface ServicePlanPriceGuideProps {
  vehicleModelsListForSelectControl: any;
  vehicleModelDataList: VehicleModelData[];
  selectedModel: string;
  showTotals: boolean;
  cmsContent: VehicleModel[];
  onSelectedModelChange: Function;
  isServicePlan: boolean;
  selectedModelVariant: string;
  handleModelVariantChange: Function;
  // New BlockList-driven props
  cpsTabs?: CmsTab[] | null;
  selectedTabId?: string | null;
  onTabChange?: (tab: CmsTab) => void;
}

const ServicePlanPriceGuide: React.SFC<ServicePlanPriceGuideProps> = observer(
  (props) => {
    const { selectedModelVariant, vehicleModelDataList } = props;

    // ── BlockList-driven tab mode ─────────────────────────────────────────────
    if (props.cpsTabs && props.cpsTabs.length > 0 && props.onTabChange) {
      const activeTab = props.cpsTabs.find((t) => t.tabId === props.selectedTabId) ?? props.cpsTabs[0];

      const isHiddenBehindButton = activeTab.displayStyle === "Hidden Behind Button";

      const colLabels = activeTab.columns.map(col => 
        col.columnHeading 
          ? col.columnHeading.replace(/<\/?p[^>]*>/g, "") 
          : col.variants.map((v) => v.variantName).filter(name => !!name).join(" / ")
      );

      let activeLabel = props.selectedModelVariant;
      if (activeLabel && !colLabels.includes(activeLabel)) {
        activeLabel = "";
      }

      const visibleColumns = isHiddenBehindButton && activeLabel
        ? activeTab.columns.filter((col, i) => colLabels[i] === activeLabel) 
        : activeTab.columns;

      // Build a flat list of all variant GUIDs for the visible columns (for interval filtering)
      const activeVariantGuids = new Set(
        visibleColumns.flatMap((col) => col.variants.flatMap((v) => [
          (v.variantGuid || "").trim().toUpperCase(),
          (v.pimIdentifier || "").trim().toUpperCase()
        ])).filter((g) => !!g)
      );

      // Resolve API data for the active tab's modelId
      const apiModel = vehicleModelDataList.find(
        (m) => m.id.toUpperCase() === (activeTab.modelId ?? "").toUpperCase()
      );

      // Deeply filter intervals and their variants to only include those in the active tab
      const serviceIntervalsList = (apiModel?.intervalData ?? [])
        .map((interval) => ({
          ...interval,
          modelVariantServicePrices: interval.modelVariantServicePrices.filter((mvsp: any) =>
            activeVariantGuids.has((mvsp.variantGUID as string || "").trim().toUpperCase()) ||
            activeVariantGuids.has((mvsp.pimIdentifier as string || "").trim().toUpperCase())
          )
        }))
        .filter((interval) =>
          interval.modelVariantServicePrices.some((mvsp: any) => !mvsp.isNotApplicable)
        );

      // Build a flat variant list for ServicePlanPriceIntervals (existing mobile component)
      // We pass a filtered intervalsList; the Intervals component shows all variants by default,
      // so we pass selectedModelVariant="" to show all in this mode.
      const tabSelectOptions = props.cpsTabs.map((tab) => ({
        value: tab.tabId,
        label: (tab.tabName || "").replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>?/gm, '').trim() || tab.modelId || "",
      }));

      const modelServicePlanContent = activeTab.modelServicePlanContent ?? "";
      const modelCappedPriceServiceContent = activeTab.modelCappedPriceServiceContent ?? "";

      return (
        <div className="service-plan-mobile">
          {/* Same DOM/CSS class — only the options and onChange are different */}
          <ServicePlanReactSelectWrap
            selectedModel={props.selectedTabId ?? ""}
            options={tabSelectOptions}
            reactSelectProps={{ clearable: false, id: "vehicleModelSelectField" }}
            onChange={(e) => {
              if (e.value) {
                const tab = props.cpsTabs!.find((t) => t.tabId === e.value);
                if (tab) props.onTabChange!(tab);
              }
            }}
          />

          {isHiddenBehindButton && (
            <div className="cappedPricingTableHeading">
              <div className="cta_wrapper">
                <h5>{activeTab.buttonText ?? "View Pricing"}</h5>
                {colLabels.map((label, i) => (
                  <a
                    key={i}
                    className={activeLabel !== label ? "cta-button" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      // handleModelVariantChange uses e.target.innerText
                      props.handleModelVariantChange(e);
                    }}
                    style={{ cursor: "pointer" }}
                  >
                    {label}
                  </a>
                ))}
              </div>
            </div>
          )}

          {(!isHiddenBehindButton || activeLabel) && (
            <div>
              <div className="panel">
                <div className="panel-heading">
                  Scheduled Service Intervals
                  <sup>{props.isServicePlan ? 2 : 1}</sup>
                </div>
                <div className="panel-body">
                  <ServicePlanPriceIntervals
                    isServicePlan={props.isServicePlan}
                    serviceIntervalsList={serviceIntervalsList}
                    showTotals={props.showTotals}
                    selectedModelVariant=""
                  />
                </div>
              </div>
              <div className="pricingDisclaimer">All prices include GST</div>
              {isHiddenBehindButton && visibleColumns.some(col => !!col.columnContent)
                ? visibleColumns.map((col, i) =>
                    col.columnContent
                      ? <div key={i} dangerouslySetInnerHTML={{ __html: col.columnContent }} />
                      : null
                  )
                : <div
                    dangerouslySetInnerHTML={{
                      __html: props.showTotals
                        ? modelServicePlanContent
                        : modelCappedPriceServiceContent,
                    }}
                  />
              }
            </div>
          )}
        </div>
      );
    }

    // ── Legacy rendering path (unchanged DOM structure & CSS) ─────────────────
    const cmsContentForSelectedModel = props.cmsContent.find(
      (x) =>
        x.modelID.toLocaleLowerCase() ===
        props.selectedModel.toLocaleLowerCase()
    );

    let modelServicePlanContent = "";
    let modelCappedPriceServiceContent = "";

    const vehicleSubNav = "Outback";

    let serviceIntervalsList = null;

    if (
      cmsContentForSelectedModel.name
        .toLocaleLowerCase()
        .includes(vehicleSubNav.toLocaleLowerCase())
    ) {
      if (
        !!selectedModelVariant &&
        selectedModelVariant.includes("Wilderness")
      ) {
        serviceIntervalsList = vehicleModelDataList
          .find(
            (apiRespModel) =>
              apiRespModel.id.toUpperCase() ===
              props.selectedModel.toUpperCase()
          )
          .intervalData.filter(
            (i) =>
              i.name.includes("Health Check and Chat") ||
              i.modelVariantServicePrices.find(
                (m) =>
                  !m.isNotApplicable && m.variantName.includes("Wilderness")
              )
          );
      } else if (
        !!selectedModelVariant &&
        selectedModelVariant.includes("XT")
      ) {
        serviceIntervalsList = vehicleModelDataList
          .find(
            (apiRespModel) =>
              apiRespModel.id.toUpperCase() ===
              props.selectedModel.toUpperCase()
          )
          .intervalData.filter(
            (i) =>
              i.name.includes("Health Check and Chat") ||
              i.modelVariantServicePrices.find(
                (m) => !m.isNotApplicable && m.variantName.includes("XT")
              )
          );
      } else {
        serviceIntervalsList = vehicleModelDataList
          .find(
            (apiRespModel) =>
              apiRespModel.id.toUpperCase() ===
              props.selectedModel.toUpperCase()
          )
          .intervalData.filter(
            (i) =>
              i.name.includes("Health Check and Chat") ||
              i.modelVariantServicePrices.find(
                (m) =>
                  !m.isNotApplicable &&
                  !(
                    m.variantName.includes("XT") ||
                    m.variantName.includes("Wilderness")
                  )
              )
          );
      }
      const selectedVariant = cmsContentForSelectedModel.variants.find(
        (v) => v.servicePriceVariantName === selectedModelVariant
      );
      if (!!selectedVariant) {
        modelServicePlanContent = selectedVariant.modelServicePlanContent;
        modelCappedPriceServiceContent =
          selectedVariant.modelCappedPriceServiceContent;
      }
    } else {
      serviceIntervalsList = props.vehicleModelDataList.find(
        (x) => x.id.toUpperCase() === props.selectedModel.toUpperCase()
      ).intervalData;
      modelServicePlanContent =
        cmsContentForSelectedModel.modelServicePlanContent;
      modelCappedPriceServiceContent =
        cmsContentForSelectedModel.modelCappedPriceServiceContent;
    }

    const ctaLabels = props.cmsContent
      .find(
        (model) =>
          model.modelID.toLocaleLowerCase() ===
          props.selectedModel.toLocaleLowerCase()
      )
      .variants.filter(
        (filterVariant) =>
          filterVariant.variantGUID !== null &&
          !!filterVariant.servicePriceVariantName
      )
      .map((label) => label.servicePriceVariantName);

    return (
      <div className="service-plan-mobile">
        <ServicePlanReactSelectWrap
          selectedModel={props.selectedModel}
          options={props.vehicleModelsListForSelectControl}
          reactSelectProps={{ clearable: false, id: "vehicleModelSelectField" }}
          onChange={(e) => {
            if (e.value) {
              const _thisModel = props.cmsContent.find(
                (x) =>
                  x.modelID.toLocaleLowerCase() === e.value.toLocaleLowerCase()
              );
              props.onSelectedModelChange(_thisModel);
            }
          }}
        />
        {cmsContentForSelectedModel.name
          .toLocaleLowerCase()
          .includes(vehicleSubNav.toLocaleLowerCase()) && (
          <div className="cappedPricingTableHeading">
            <div className="cta_wrapper">
              <h5>
                Please select your {cmsContentForSelectedModel.name} variant
              </h5>
              {ctaLabels.map((label, i) => {
                return (
                  <a
                    key={i}
                    className={
                      selectedModelVariant !== label ? "cta-button" : ""
                    }
                    onClick={(e) => props.handleModelVariantChange(e)}
                  >
                    {label}
                  </a>
                );
              })}
            </div>
          </div>
        )}

        {(!cmsContentForSelectedModel.name
          .toLocaleLowerCase()
          .includes(vehicleSubNav.toLocaleLowerCase()) ||
          (cmsContentForSelectedModel.name
            .toLocaleLowerCase()
            .includes(vehicleSubNav.toLocaleLowerCase()) &&
            !!selectedModelVariant)) && (
          <div>
            <div className="panel">
              <div className="panel-heading">
                Scheduled Service Intervals
                <sup>{props.isServicePlan ? 2 : 1}</sup>
              </div>
              <div className="panel-body">
                <ServicePlanPriceIntervals
                  isServicePlan={props.isServicePlan}
                  serviceIntervalsList={serviceIntervalsList}
                  showTotals={props.showTotals}
                  selectedModelVariant={selectedModelVariant}
                ></ServicePlanPriceIntervals>
              </div>
            </div>
            <div className="pricingDisclaimer">All prices include GST</div>
            <div
              dangerouslySetInnerHTML={{
                __html: props.showTotals
                  ? modelServicePlanContent
                  : modelCappedPriceServiceContent,
              }}
            />
          </div>
        )}
      </div>
    );
  }
);

export default ServicePlanPriceGuide;
